/**
 * The tour: a spotlight walkthrough built like Ironwood's and the family tree's. One animation
 * loop springs the highlight and the text card toward their targets; changing tabs fades the
 * page out, swaps it while it's invisible, and fades it back in. It runs once on the first visit
 * (skippable) and again from Settings → Take the tour.
 *
 * While it runs, the screens show a sample meadow garden instead of the player's own, the way
 * Ironwood shows sample workouts. The sample lives only in memory and goes away when the tour
 * ends; nothing in it reaches the server.
 */
import {
  applyAction, DEFAULT_RULES, findOpenSpot, GROWTH_STAGES, newGame, runCron,
  type Action, type GameState, type GrowthStage, type Rules,
} from '@thicket/core';
import type { GameStore } from '../store.js';
import { app, update, type Tab } from './app.js';
import { setDrawer } from './garden.js';
import { startLab } from './labLoad.js';

const DONE_KEY = 'thicket.tourDone';

/* ---------- the sample garden ---------- */

/** A read-only store holding the sample garden; the tour's overlay keeps taps from reaching it. */
class TourStore implements GameStore {
  readonly queue: readonly unknown[] = [];
  readonly offline = false;
  constructor(public view: GameState, public rules: Rules, private clock: () => Date) {}
  now() {
    return this.clock();
  }
  subscribe() {
    return () => {};
  }
  async load() {}
  async refresh() {}
  async flush() {}
  onRemoteVersion() {}
  act() {
    return false;
  }
}

function sampleGarden(rules: Rules, timezone: string, now: Date): GameState {
  const uuid = () =>
    typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) => (Number(c) ^ (crypto.getRandomValues(new Uint8Array(1))[0]! & (15 >> (Number(c) / 4)))).toString(16));
  const ctx = { now, newId: uuid, random: () => 0.99, rules, source: 'app' as const };
  let s = newGame({ timezone, starterBiome: 'meadow' }, ctx);
  s = runCron(s, ctx).state;
  const act = (a: Action) => {
    try {
      s = applyAction(s, a, ctx).state;
    } catch {
      // A sample step that doesn't fit the current rules is simply left out.
    }
  };
  const add = (task: Record<string, unknown>) => {
    const id = uuid();
    act({ type: 'createTask', task: { ...task, id } } as Action);
    return id;
  };
  const dailies = [
    add({ type: 'daily', text: 'Morning walk', difficulty: 'medium', label: 'Walk' }),
    add({ type: 'daily', text: 'Anki reviews', difficulty: 'easy', label: 'Anki' }),
    add({ type: 'daily', text: 'Gym', difficulty: 'hard', label: 'Gym' }),
  ];
  const habits = [
    add({ type: 'habit', text: 'Drink water', difficulty: 'trivial', up: true, down: false }),
    add({ type: 'habit', text: 'Read before bed', difficulty: 'easy', up: true, down: false }),
    add({ type: 'habit', text: 'Scroll past midnight', difficulty: 'easy', up: true, down: true }),
  ];
  const todos = [
    add({ type: 'todo', text: 'Email my advisor', difficulty: 'easy' }),
    add({ type: 'todo', text: 'Outline the paper', difficulty: 'medium' }),
    add({ type: 'todo', text: 'Book a dentist visit', difficulty: 'trivial' }),
  ];
  // Plant the trees and buds on open meadow ground, and the habit plants in the bed.
  let plot = 0;
  for (const p of s.plants) {
    if (p.goneAt) continue;
    if ((p.kind === 'tree' || p.kind === 'wildflower') && p.biome === null) {
      const spot = findOpenSpot(s, 'meadow', 'center', p.id, p);
      if (spot) act({ type: 'placePlant', plantId: p.id, spot: { where: 'ground', biome: 'meadow', ...spot } });
    } else if (p.kind === 'bed' && p.plot === null) act({ type: 'placePlant', plantId: p.id, spot: { where: 'bed', plot: plot++ } });
  }
  // Show a few stages of growth, the way a garden looks a few weeks in.
  const grow = (taskId: string, stage: GrowthStage) => {
    const p = s.plants.find((x) => x.taskId === taskId && x.kind !== 'weed' && !x.goneAt);
    if (!p) return;
    const i = GROWTH_STAGES.indexOf(stage);
    p.stage = stage;
    p.completions = i === 0 ? 0 : rules.stageCounts[p.pace][i - 1]!;
    p.streak = 6;
  };
  grow(dailies[0]!, 'large');
  grow(dailies[1]!, 'medium');
  grow(dailies[2]!, 'small');
  grow(habits[0]!, 'large');
  grow(habits[1]!, 'medium');
  act({ type: 'scoreTask', id: dailies[0]!, direction: 'up' });
  act({ type: 'scoreTask', id: habits[0]!, direction: 'up' });
  act({ type: 'scoreTask', id: habits[2]!, direction: 'down' });
  act({ type: 'scoreTask', id: todos[0]!, direction: 'up' });
  s.profile.coins = Math.max(s.profile.coins, 64);
  s.profile.xp = Math.max(s.profile.xp, 140);
  act({ type: 'buyAnimal', kind: 'songbird' });
  s.profile.coins = 64;
  return s;
}

/* ---------- steps ---------- */

interface Step {
  tab: Tab;
  /** What to highlight; none centres the card. */
  sel?: string;
  /** How many matches to highlight together. */
  max?: number;
  t: string;
  b: string;
  /** Runs before the highlight is placed. */
  prep?: () => void;
}

/** Opens or closes the garden drawer and scrolls only the drawer (never the page) to a section in it. */
const drawer = (open: boolean, scrollTo?: string) => () => {
  setDrawer(open);
  requestAnimationFrame(() => {
    const body = document.querySelector<HTMLElement>('.drawer-body');
    const el = scrollTo ? document.querySelector<HTMLElement>(scrollTo) : null;
    if (body) body.scrollTop = el ? body.scrollTop + el.getBoundingClientRect().top - body.getBoundingClientRect().top - 8 : 0;
    document.querySelector('.shell')?.scrollTo(0, 0);
  });
};

const TOUR: Step[] = [
  { tab: 'dailies', t: 'Welcome to Thicket', b: 'A quick look at how your routine grows a garden. It takes about a minute, and you can skip it any time.' },
  { tab: 'dailies', sel: '#dailies', t: 'Dailies grow trees', b: 'Each daily is a tree. Check it off and the tree grows. A day you miss never kills it; the leaves thin, and fill back in when you start again. These tasks are samples for the tour; they disappear when it ends.' },
  { tab: 'habits', sel: '#habits', t: 'Habits fill the raised bed', b: 'Tap + when you do one and its plant grows. A − sprouts a small weed that never hurts anything and fades on its own.' },
  { tab: 'todos', sel: '#todos', t: 'To-dos become wildflowers', b: 'Each to-do starts as a bud. Finish it and it blooms, bigger for harder tasks. An overdue bud droops a little but never dies.' },
  { tab: 'todos', sel: '.fab', t: 'Add anything', b: 'Every tab has a + for its own kind of task. Difficulty picks the tree or flower and how fast it grows.' },
  { tab: 'todos', sel: '.stats', t: 'Coins and levels', b: 'Everything you finish pays coins and XP. Coins buy companions for your garden.' },
  { tab: 'garden', sel: '.scene-frame', prep: drawer(false), t: 'Your garden', b: 'Everything you grow lives here. Drag to look around, pinch to zoom, and tap a plant to see how it is doing.' },
  { tab: 'garden', sel: '.drawer-handle, .garden-side', prep: drawer(false), t: 'Planting', b: 'New trees and buds wait in here until you plant them. Pick one, then tap open grass. A tree is planted once, so choose a spot you like.' },
  { tab: 'garden', sel: '#bed', prep: drawer(true, '#bed'), t: 'The raised bed', b: 'Your habit plants grow in these plots. Tap a plot with a weed to pull it.' },
  { tab: 'garden', sel: '#shop', prep: drawer(true, '#shop'), t: 'Companions', b: 'Spend coins on a companion. Songbirds, squirrels and owls need a tree big enough to live in; bees, toads and butterflies keep the bed company. Once they move in, they stay.' },
  { tab: 'journal', sel: '#field-journal', prep: drawer(false), t: 'Field Journal', b: 'Every tree with its stage and streak, and how close it is to growing. You never have to visit the garden to know what needs you.' },
  { tab: 'settings', sel: '#rest', t: 'Rest when you need to', b: 'A hard week? Pause the whole garden and nothing changes until you are back. You can also rest a single daily.' },
  { tab: 'settings', sel: '#tour-btn', t: 'Take the tour again', b: 'This button brings the tour back whenever you want it.' },
  { tab: 'dailies', t: "That's the tour", b: 'Add your first daily and plant its tree.' },
];

/* ---------- the overlay ---------- */

const TS = {
  on: false,
  step: 0,
  token: 0,
  raf: 0,
  real: null as GameStore | null,
  from: null as HTMLElement | null,
  tgt: { els: null as Element[] | null, mid: true, side: 'mid' as 'top' | 'bottom' | 'mid', show: false },
};
let nav: (tab: Tab) => void = () => {};
const $ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document) => root.querySelector<T>(sel);
const reduceMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));
const frame = () => new Promise<void>((r) => requestAnimationFrame(() => r()));
const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

/** Main gives the tour its way to change tabs. */
export function tourNav(fn: (tab: Tab) => void) {
  nav = fn;
}

export const tourOn = () => TS.on;

function mount() {
  const el = document.createElement('div');
  el.className = 'tour';
  el.id = 'tour';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.innerHTML = `<div class="tour-hole"><svg class="tour-dim" aria-hidden="true"><defs><mask id="tour-mask" maskUnits="userSpaceOnUse">
    <rect x="0" y="0" width="100%" height="100%" fill="#fff"/><rect class="tour-cut" x="0" y="0" width="0" height="0" fill="#000"/></mask></defs>
    <rect x="0" y="0" width="100%" height="100%" fill="rgba(8,16,12,.62)" mask="url(#tour-mask)"/></svg><div class="tour-ring"></div></div>
    <div class="tour-safe" aria-hidden="true"></div><div class="tour-card"><div class="tour-txt"></div><div class="trow"><button type="button" id="tour-back">Back</button><button type="button" class="primary" id="tour-next">Next</button></div></div>`;
  document.body.appendChild(el);
  $('#tour-back', el)!.onclick = () => go(TS.step - 1);
  $('#tour-next', el)!.onclick = () => (TS.step >= TOUR.length - 1 ? endTour() : go(TS.step + 1));
  el.addEventListener('click', (e) => {
    if ((e.target as Element).closest('#tour-skip')) endTour();
  });
  return el;
}

/** Space the card must leave for the tab bar at the bottom and the header at the top. */
function edges() {
  const tabs = $('nav.tabs');
  const bottom = tabs && getComputedStyle(tabs).display !== 'none' ? innerHeight - tabs.getBoundingClientRect().top : 0;
  // On a home-screen install the page runs under the clock and battery; keep clear of them.
  const top = $('.tour-safe')?.offsetHeight ?? 0;
  return { top, bottom: Math.max(0, bottom) };
}

/**
 * iOS can leave fixed layers (the tab bar, the dimming) drawn where they were before a scroll the
 * page made itself, so they hang mid-screen. Taking them out of layout and back forces a redraw.
 */
function refixLayers() {
  for (const el of document.querySelectorAll<HTMLElement>('.tour-hole, .tour-card, nav.tabs')) {
    el.style.transition = 'none'; // so the card doesn't fade out and back in
    el.style.display = 'none';
    void el.offsetHeight;
    el.style.display = '';
    void el.offsetHeight;
    el.style.transition = '';
  }
}

function loop() {
  const el = $('#tour')!;
  const cut = $('.tour-cut', el)!;
  const ring = $('.tour-ring', el)!;
  const card = $('.tour-card', el)!;
  const cur = { x: innerWidth / 2, y: innerHeight / 2, w: 0, h: 0, ring: 0, cy: null as number | null, op: 0 };
  const vel = { x: 0, y: 0, w: 0, h: 0, cy: 0 };
  const W = 11;
  type K = 'x' | 'y' | 'w' | 'h' | 'cy';
  const spring = (p: K, goal: number, dt: number) => {
    const a = W * W * (goal - (cur[p] as number)) - 2 * W * vel[p];
    vel[p] += a * dt;
    (cur[p] as number) += vel[p] * dt;
  };
  let prev = performance.now();
  let last: { l: number; t: number; r: number; b: number } | null = null;
  const tick = (now: number) => {
    if (!TS.on) return;
    const dt = Math.min(64, now - prev);
    prev = now;
    const rm = reduceMotion();
    const k = 1 - Math.exp(-dt / (rm ? 60 : 85));
    const sdt = Math.min(0.032, dt / 1000);
    const snap = rm && (cur.op < 0.04 || cur.cy === null);
    const T = TS.tgt;
    const vw = innerWidth;
    const vh = innerHeight;
    const pad = 6;
    let r: typeof last = null;
    if (T.els) for (const e of T.els) {
      if (!e.isConnected) continue;
      const q = e.getBoundingClientRect();
      if (!q.width) continue;
      r = r ? { l: Math.min(r.l, q.left), t: Math.min(r.t, q.top), r: Math.max(r.r, q.right), b: Math.max(r.b, q.bottom) } : { l: q.left, t: q.top, r: q.right, b: q.bottom };
    }
    if (r) last = r;
    const use = T.mid ? null : r ?? last;
    const safeTop = edges().top;
    const g = use
      ? { x: Math.max(4, use.l - pad), y: Math.max(safeTop + 4, use.t - pad), w: Math.min(vw - 8, use.r - use.l + 2 * pad), h: Math.min(vh - 8, use.b - use.t + 2 * pad), ring: 1 }
      : { x: vw / 2, y: vh / 2, w: 0, h: 0, ring: 0 };
    for (const p of ['x', 'y', 'w', 'h'] as const) {
      if (snap) {
        cur[p] = g[p];
        vel[p] = 0;
      } else if (!rm) spring(p, g[p], sdt);
      else cur[p] = g[p];
    }
    cur.ring += (g.ring - cur.ring) * k;
    cut.setAttribute('fill-opacity', (rm ? cur.op : 1).toFixed(3));
    cut.setAttribute('x', cur.x.toFixed(1));
    cut.setAttribute('y', cur.y.toFixed(1));
    cut.setAttribute('width', Math.max(0, cur.w).toFixed(1));
    cut.setAttribute('height', Math.max(0, cur.h).toFixed(1));
    ring.style.transform = `translate(${cur.x.toFixed(1)}px,${cur.y.toFixed(1)}px)`;
    ring.style.width = `${Math.max(0, cur.w).toFixed(1)}px`;
    ring.style.height = `${Math.max(0, cur.h).toFixed(1)}px`;
    ring.style.opacity = (cur.ring * (rm ? cur.op : 1)).toFixed(3);
    const { top, bottom } = edges();
    const ch = card.offsetHeight;
    const side = T.mid ? 'mid' : T.side;
    const ty = side === 'top' ? top + 12 : side === 'bottom' ? vh - bottom - 12 - ch : (vh - ch) / 2;
    if (cur.cy === null || snap) {
      cur.cy = ty;
      vel.cy = 0;
    } else if (!rm) spring('cy', ty, sdt);
    else cur.cy = ty;
    cur.op += ((T.show ? 1 : 0) - cur.op) * k;
    card.style.transform = `translateY(${cur.cy!.toFixed(1)}px)`;
    card.classList.toggle('on', T.show);
    TS.raf = requestAnimationFrame(tick);
  };
  TS.raf = requestAnimationFrame(tick);
}

function text(i: number) {
  const s = TOUR[i]!;
  const last = i >= TOUR.length - 1;
  const el = $('#tour')!;
  const box = $('.tour-txt', el)!;
  const nb = box.cloneNode(false) as HTMLElement;
  box.replaceWith(nb);
  nb.innerHTML = `<div class="trow"><div class="eyebrow">${i + 1} of ${TOUR.length}</div>${last ? '' : '<button type="button" class="tour-skip" id="tour-skip">Skip tour</button>'}</div>
    <h2 id="tour-h">${esc(s.t)}</h2><p>${esc(s.b)}</p>`;
  el.setAttribute('aria-labelledby', 'tour-h');
  $<HTMLButtonElement>('#tour-back', el)!.hidden = i === 0;
  $('#tour-next', el)!.textContent = last ? 'Done' : i === 0 ? 'Show me' : 'Next';
}

async function show(step: number) {
  const my = ++TS.token;
  const alive = () => TS.on && my === TS.token;
  const T = TS.tgt;
  const s = TOUR[step]!;
  const rm = reduceMotion();
  if (rm && T.show) {
    T.show = false;
    await wait(220);
    if (!alive()) return;
  }
  text(step);
  $('#tour-next')?.focus({ preventScroll: true });
  const needTab = app.tab !== s.tab;
  if (needTab) {
    document.body.classList.add('tour-fading');
    await wait(200);
    if (!alive()) return;
    nav(s.tab);
    await frame();
    await frame();
  }
  if (s.prep) {
    s.prep();
    await frame();
    await wait(rm ? 0 : 240); // let the drawer slide
    $('#tour-next')?.focus({ preventScroll: true });
  }
  let els: Element[] = [];
  if (s.sel) for (let i = 0; i < 30 && alive(); i++) {
    els = [...document.querySelectorAll(s.sel)].filter((e) => !(e as HTMLElement).hidden && e.getBoundingClientRect().width > 0).slice(0, s.max ?? 1);
    if (els.length) break;
    await wait(50);
  }
  if (!alive()) return;
  if (els.length) {
    const fixed = els.every((e) => getComputedStyle(e).position === 'fixed' || !!e.closest('.shell.full'));
    let r: { t: number; b: number } | null = null;
    for (const e of els) {
      const q = e.getBoundingClientRect();
      r = r ? { t: Math.min(r.t, q.top), b: Math.max(r.b, q.bottom) } : { t: q.top, b: q.bottom };
    }
    const vh = innerHeight;
    const maxY = Math.max(0, document.documentElement.scrollHeight - vh);
    const { top, bottom } = edges();
    const ch = $('.tour-card')!.offsetHeight;
    // The space the card leaves free on each side; the target is scrolled to sit in it.
    const free = { bottom: [top + 12, vh - bottom - ch - 24], top: [top + ch + 24, vh - bottom - 12] } as const;
    const place = (side: 'top' | 'bottom') => {
      const [a, b] = free[side];
      const ideal = scrollY + (r!.t + r!.b) / 2 - (a + b) / 2;
      const y = fixed ? scrollY : Math.min(maxY, Math.max(0, r!.b - r!.t > b - a ? scrollY + r!.t - a : ideal));
      const t = r!.t - (y - scrollY);
      const miss = Math.max(0, a - t) + Math.max(0, t + (r!.b - r!.t) - b);
      return { y, miss };
    };
    const below = place('bottom');
    const above = place('top');
    // The card goes where it covers the least of the target; ties keep it at the bottom.
    T.side = above.miss < below.miss - 1 ? 'top' : 'bottom';
    const want = (T.side === 'top' ? above : below).y;
    if (Math.abs(want - scrollY) > 40 && !needTab) {
      document.body.classList.add('tour-fading');
      await wait(180);
      if (!alive()) return;
    }
    scrollTo(0, want);
    await frame();
    refixLayers();
    T.mid = false;
    T.els = els;
    T.show = true;
  } else {
    T.mid = true;
    T.els = null;
    T.show = true;
  }
  document.body.classList.remove('tour-fading');
}

function go(n: number) {
  if (n < 0 || n >= TOUR.length) return;
  TS.step = n;
  void show(n);
}

function onKey(e: KeyboardEvent) {
  if (!TS.on) return;
  const typing = (e.target as Element | null)?.closest?.('input,select,textarea');
  if (e.key === 'Escape') {
    e.stopImmediatePropagation();
    endTour();
  } else if (e.key === 'ArrowRight' && !typing) {
    if (TS.step >= TOUR.length - 1) endTour();
    else go(TS.step + 1);
  } else if (e.key === 'ArrowLeft' && !typing) go(TS.step - 1);
  else if (e.key === 'Tab') {
    // Keep focus inside the tour card.
    const f = [...document.querySelectorAll<HTMLButtonElement>('#tour button:not([hidden])')];
    if (!f.length) return;
    const i = f.indexOf(document.activeElement as HTMLButtonElement);
    e.preventDefault();
    f[(i + (e.shiftKey ? -1 : 1) + f.length) % f.length]!.focus();
  }
}

/** Starts the tour, with the sample garden standing in for the player's own. */
export function startTour() {
  if (TS.on || !app.store.view) return;
  const real = app.store;
  const rules = real.rules ?? DEFAULT_RULES;
  const now = real.now();
  const sample = sampleGarden(rules, real.view!.profile.timezone, now);
  TS.real = real;
  TS.from = document.activeElement as HTMLElement | null;
  update(() => {
    app.store = new TourStore(sample, rules, () => real.now());
    app.sheet = null;
    app.planting = null;
    app.biome = null;
    app.immersive = false;
  });
  TS.on = true;
  TS.step = 0;
  TS.tgt = { els: null, mid: true, side: 'mid', show: false };
  mount();
  document.body.classList.add('tour-on');
  loop();
  void show(0);
  document.addEventListener('keydown', onKey, true);
}

/** Ends the tour and puts the player's own garden back. */
export function endTour() {
  if (!TS.on) return;
  TS.on = false;
  TS.token++;
  cancelAnimationFrame(TS.raf);
  document.removeEventListener('keydown', onKey, true);
  $('#tour')?.remove();
  document.body.classList.remove('tour-on', 'tour-fading');
  try {
    localStorage.setItem(DONE_KEY, '1');
  } catch {
    // Only a preference.
  }
  setDrawer(false);
  update(() => {
    if (TS.real) app.store = TS.real;
    TS.real = null;
  });
  nav('dailies');
  scrollTo(0, 0);
  (TS.from?.isConnected ? TS.from : null)?.focus({ preventScroll: true });
}

/** On a first visit, the tour starts by itself (once). */
export function maybeStartTour() {
  let done = false;
  try {
    done = localStorage.getItem(DONE_KEY) === '1';
  } catch {
    done = true;
  }
  if (done) return;
  // Wait for the garden art, so the first card doesn't fade in over a page that's still loading.
  const start = () => setTimeout(() => startTour(), 600);
  Promise.race([startLab(), wait(10_000)]).then(start, start);
}
